import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { isVmiAdmin } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import createShipTo from "@insite/client-framework/Store/Pages/Addresses/Handlers/CreateShipTo";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddIcon from "@MasterBluePrint/Icons/AddIcon";
import CustomerAddressForm from "@MasterBluePrint/Overrides/Components/CustomerAddressForm";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const settings = getSettingsCollection(state);
    return {
        customerSettings: settings.customerSettings,
        currentBillTo: getCurrentBillToState(state).value,
        newShipTo: state.pages.addresses.newShipTo,
        countries: getCurrentCountries(state),
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        location: getLocation(state),
        isVmiAdmin: isVmiAdmin(settings.orderSettings, session),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    };
};

const mapDispatchToProps = {
    createShipTo,
    setCurrentShipTo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAddressButtonStyles {
    gridContainer?: GridContainerProps;
    createNewAddressButtonGridItem?: GridItemProps;
    overflowMenu?: OverflowMenuPresentationProps;
    createNewAddressButton?: ButtonPresentationProps;
    plusSign?: InjectableCss;
    addressFormModalCustom?: InjectableCss;
    addressFormModal?: ModalPresentationProps;
    closeContainer?: InjectableCss;
}

export const createAddressButtonStyles: CreateAddressButtonStyles = {
    closeContainer: {
        css: css`
            position: absolute;
            left: 1.8rem;
            top: -5rem;
            z-index: 9;
            cursor: pointer;
            .close-label {
                color: ${themeColors.colorBrandsSeeMore};
                font-family: Roboto;
                font-size: 0.875rem;
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: 0.525rem;
                vertical-align: middle;
            }
            .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1rem;
                height: 1rem;
                svg {
                    height: 1rem;
                    width: 1rem;
                    color: ${themeColors.colorBrandsSeeMore};
                }
            }
            @media screen and (max-width: 990px) {
                right: 1.25rem;
            }
        `,
    },
    createNewAddressButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            background: ${themeColors.colorWhite};
            border-radius: 0.125rem;
            box-shadow: 0 0.125rem 0.25rem 0.25rem rgba(0,0,0,0.03);
            padding: 2.875rem 2rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 1.5rem 2.25rem 1.5rem 1.875rem;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    addressFormModal: { sizeVariant: "medium" },
    addressFormModalCustom: {
        css:css`
            div[class^='ModalContent'], 
            div[class*=' ModalContent']{              
                position: relative;  
                padding-bottom: 11.1875rem !important; 
                height: calc(100vh - 13.1875rem);
                
                @media screen and (max-width: 767px) {
                    padding-bottom: 10.1875rem !important;
                    height: calc(100vh - 13.1875rem);
                }

                @media screen and (max-width: 540px) { 
                    height: calc(100vh - 11.8rem);
                    padding-bottom: 11.8rem !important;
                }
            }
            > div {
                max-height: 100vh;
                margin: 0 0 0 auto;
                max-width: 35.625rem;
                border: 1px solid rgba(7,15,44,0.08);
                border-radius: 1px;
                background-color: ${themeColors.colorWhite};

                @media (max-width: 767px) {
                    max-width: 100%;
                    margin-right: 0;
                    margin-left: 0;
                }

                > div:last-child {
                    padding: 0;
                    > form {
                        > div {
                            padding: 1.875rem;   
                        }

                        > div:last-child {
                            border-radius: 1px;
                            background: ${themeColors.colorWhite};
                            box-shadow: 0 -0.125rem 0.375rem 0 rgba(0,0,0,0.31);
                            padding-left: 0;
                            padding-right: 0;

                            @media screen and (max-width: 767px) {
                                box-shadow: none;
                            }
                        }
                        @media screen and (max-width: 326px) {
                            > div {
                                padding: 1.875rem 1.875rem 2.2rem;                           
                            }
                            > div:last-child {
                                padding-top: 7.5rem;
                                padding-bottom: 3.875rem;
                            }                            
                        }
                        @media screen and (min-width: 360px) and (max-width: 369px) {                            
                            > div:last-child {
                                padding-top: 5rem;
                            }                            
                        }
                        @media screen and (min-width: 370px) and (max-width: 379px) {                            
                            > div:last-child {
                                padding-top: 2.8rem;
                            }                            
                        }
                        @media screen and (min-width: 380px) and (max-width: 414px) {                            
                            > div:last-child {
                                padding-top: 3rem;
                            }                            
                        }
                    }
                }
                #modalTitle{
                    border-bottom: none;
                    padding-bottom: 0px;

                    h4 {
                        color: ${themeColors.colorPrimary};
                        font-family: Roboto;
                        font-size: 1.125rem;
                        font-weight: ${themeFontWeights.fontWeightMedium};
                        letter-spacing: 0px;
                        line-height: 1.9375rem;
                        padding-top: 4.0625rem;
                        position: relative;
                        &:after {
                            height: 0.1875rem;
                            width: 2.75rem;
                            background: ${themeColors.colorRed};
                            content: "";
                            display: inline-block;
                            position: absolute;
                            bottom: -0.1875rem;
                            left: 1px;
                        }
                    }
                    button[aria-labelledby="close-modal"] {
                        display: none;
                    }
                }
            }
        `,
    },
    createNewAddressButton: {
        css:css`
            font-family: 'Roboto', sans-serif;
            width: 100%;
            border-radius: 0.125rem;
            background: ${themeColors.colorPrimary};
            border: 0.125rem solid ${themeColors.colorPrimary};
            color: ${themeColors.colorWhite};
            padding: 0.75rem 2.625rem;
            text-transform: uppercase;
            font-weight: ${themeFontWeights.fontWeightForBold};
            font-size: 0.875rem;
            display: flex;
            align-items: center;
            justify-content: center;            

            span[class*='IconWrapper'],
            span[class^='IconWrapper'] {
                width: 0.875rem;
                height: 0.875rem;
                margin-right: 11px;
            }
            svg {
                width: 0.875rem;
                height: 0.875rem;
            }
            &:hover,  &:focus {
                color: #fff;
                background-color: ${themeColors.colorPrimary} !important;
                border-color: ${themeColors.colorPrimary} !important;
            }

            @media screen and (min-width: 991px) and (max-width: 1335px) {
                padding: 0.75rem 1.875rem;
            }

            @media screen and (min-width: 768px) and (max-width: 990px) {
                padding: 0.75rem 0.625rem;
                font-size: 0.75rem;
            }
        `,
    },
    plusSign: {
        css:css`
            margin-right: 0.6875rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
        `,        
    },
};

const styles = createAddressButtonStyles;

const CreateAddressButton = ({
    customerSettings,
    currentBillTo,
    newShipTo,
    countries,
    billToAddressFields,
    location,
    isVmiAdmin,
    createShipTo,
    setCurrentShipTo,
    shipToAddressFields
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    useEffect(() => {
        const parsedQuery = parseQueryString<{ isNewShipTo: string }>(location.search);
        if (parsedQuery.isNewShipTo === "true") {
            setModalIsOpen(true);
        }
    }, []);

    if (!customerSettings.allowCreateNewShipToAddress && !isVmiAdmin) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {     
        if (!currentBillTo || !newShipTo) {
            return;
        }
        createShipTo({
            billToId: currentBillTo.id,
            shipTo: {
                ...newShipTo,
                ...address,
            },
            onComplete: result => {
                if (result.apiResult && result.apiParameter?.shipTo.isDefault) {
                    setCurrentShipTo({ shipToId: result.apiResult.id });
                }
                modalCloseHandler();
           },            
        });
        // modalCloseHandler();
       
    };

    return (
        <GridContainer {...styles.gridContainer} data-test-selector="createAddressButtonContainer">
            <GridItem {...styles.createNewAddressButtonGridItem} data-test-selector="createAddressButtonGrid">
                
                
                    <Button
                        {...styles.createNewAddressButton}
                        onClick={editClickHandler}
                        data-test-selector="addresses_CreateNewAddress"
                    >
                        <Icon src={AddIcon} />
                        {translate(" Add a New Address")}
                    </Button>
                
                <Modal
                    headline={translate("Add a new address")}
                    {...styles.addressFormModal}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                    {...styles.addressFormModalCustom} data-test-selector="newAddress_formModal"
                >
                    <Typography as="div" onClick={modalCloseHandler} {...styles.closeContainer}>
                        <span className="close-label">Close</span>
                        <Icon className="icon-close" src="XCircle" />
                    </Typography>
                    {newShipTo && countries && shipToAddressFields && (
                        <CustomerAddressForm
                            address={newShipTo}
                            countries={countries}
                            addressFieldDisplayCollection={shipToAddressFields}
                            onCancel={formCancelHandler}
                            onSubmit={formSubmitHandler}
                        />
                    )}
                </Modal>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAddressButton),
    definition: {
        group: "Addresses",
        icon: "rectangle-wide",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
